<script setup>
import { ref, computed } from 'vue';
import { RouterLink } from 'vue-router';

const selectedCategory = ref('all');

const categories = [
  { id: 'all', name: 'Toutes les cartes', icon: 'card_giftcard' },
  { id: 'streaming', name: 'Streaming', icon: 'play_circle' },
  { id: 'music', name: 'Musique', icon: 'music_note' },
];

const giftCards = ref([
  {
    id: 'netflix-gift',
    name: 'Carte Cadeau Netflix',
    minPrice: 2500,
    maxPrice: 25000,
    color: 'from-red-500 to-red-700',
    icon: 'play_circle',
    category: 'streaming',
  },
  {
    id: 'spotify-gift',
    name: 'Carte Cadeau Spotify',
    minPrice: 3000,
    maxPrice: 30000,
    color: 'from-green-500 to-green-700',
    icon: 'music_note',
    category: 'music',
  },
  {
    id: 'apple-gift',
    name: 'Carte Cadeau Apple',
    minPrice: 5000,
    maxPrice: 50000,
    color: 'from-slate-700 to-slate-900',
    icon: 'apple',
    category: 'streaming',
  },
  {
    id: 'disney-gift',
    name: 'Carte Cadeau Disney+',
    minPrice: 3800,
    maxPrice: 38000,
    color: 'from-blue-600 to-indigo-800',
    icon: 'movie',
    category: 'streaming',
  },
  {
    id: 'amazon-gift',
    name: 'Carte Cadeau Amazon',
    minPrice: 5000,
    maxPrice: 100000,
    color: 'from-orange-500 to-amber-600',
    icon: 'shopping_bag',
    category: 'streaming',
  },
  {
    id: 'googleplay-gift',
    name: 'Carte Cadeau Google Play',
    minPrice: 2000,
    maxPrice: 50000,
    color: 'from-slate-600 to-slate-800',
    icon: 'play_arrow',
    category: 'streaming',
  },
  {
    id: 'playstation-gift',
    name: 'Carte Cadeau PlayStation',
    minPrice: 10000,
    maxPrice: 50000,
    color: 'from-blue-500 to-blue-700',
    icon: 'sports_esports',
    category: 'streaming',
  },
  {
    id: 'xbox-gift',
    name: 'Carte Cadeau Xbox',
    minPrice: 10000,
    maxPrice: 50000,
    color: 'from-green-600 to-emerald-800',
    icon: 'videogame_asset',
    category: 'streaming',
  },
]);

const filteredGiftCards = computed(() => {
  if (selectedCategory.value === 'all') return giftCards.value;
  return giftCards.value.filter(card => card.category === selectedCategory.value);
});
</script>

<template>
  <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">

    <!-- Breadcrumb -->
    <nav aria-label="Breadcrumb" class="flex items-center text-sm text-slate-500 dark:text-slate-400 mb-8">
      <RouterLink class="hover:text-primary transition-colors" to="/store">Store</RouterLink>
      <span class="mx-2">»</span>
      <span class="font-medium text-slate-900 dark:text-slate-200">Cartes Cadeaux</span>
    </nav>

    <!-- Hero Section -->
    <section class="rounded-3xl p-8 md:p-12 mb-12" style="background: linear-gradient(135deg, rgba(56, 189, 248, 0.1) 0%, rgba(124, 58, 237, 0.1) 100%);">
      <div class="max-w-3xl">
        <h1 class="text-3xl md:text-4xl font-extrabold mb-4">Cartes Cadeaux</h1>
        <p class="text-slate-600 dark:text-slate-400 text-lg leading-relaxed">
          Offrez le parfait. Nos cartes cadeaux sont valables sur tous nos abonnements numériques.
          Choisissez le montant, personnalisez votre message et faites plaisir à vos proches.
        </p>
      </div>
    </section>

    <!-- Category Cards -->
    <div class="grid grid-cols-1 md:grid-cols-3 gap-6 mb-16">
      <button
        v-for="category in categories"
        :key="category.id"
        @click="selectedCategory = category.id"
        class="group bg-white dark:bg-card-dark rounded-2xl p-6 border transition-all hover:border-primary"
        :class="selectedCategory === category.id ? 'border-primary' : 'border-slate-200 dark:border-slate-700'"
      >
        <div class="flex items-center gap-4">
          <div class="w-14 h-14 rounded-xl flex items-center justify-center transition-colors"
            :class="selectedCategory === category.id ? 'bg-primary/20' : 'bg-primary/10 group-hover:bg-primary/20'">
            <span class="material-icons-outlined text-2xl"
              :class="selectedCategory === category.id ? 'text-primary' : category.id === 'streaming' ? 'text-emerald-500' : category.id === 'music' ? 'text-violet-500' : 'text-primary'">
              {{ category.icon }}
            </span>
          </div>
          <div class="text-left">
            <h3 class="font-bold text-lg">{{ category.name }}</h3>
            <p v-if="category.id !== 'all'" class="text-sm text-slate-500">
              {{ category.id === 'streaming' ? 'Netflix, Disney+, etc.' : 'Spotify, Apple Music' }}
            </p>
            <p v-else class="text-sm text-slate-500">Voir tout</p>
          </div>
        </div>
      </button>
    </div>

    <!-- Gift Cards Grid -->
    <section>
      <div class="flex items-center justify-between mb-8 border-b border-slate-200 dark:border-slate-800 pb-4">
        <h2 class="text-3xl font-extrabold tracking-tight">Nos Cartes Cadeaux</h2>
        <div class="flex items-center gap-2 text-sm text-slate-500">
          <span>Affichage de 1–{{ filteredGiftCards.length }} sur {{ giftCards.length }} résultats</span>
        </div>
      </div>

      <div class="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-6">
        <RouterLink
          v-for="card in filteredGiftCards"
          :key="card.id"
          :to="`/produit/${card.id}`"
          class="group cursor-pointer"
        >
          <div class="aspect-[4/5] rounded-2xl overflow-hidden mb-4 shadow-lg group-hover:shadow-xl transition-all duration-300 transform group-hover:-translate-y-1 relative"
            :class="`bg-gradient-to-br ${card.color}`">
            <div class="absolute inset-0 flex flex-col items-center justify-center p-6 text-white">
              <span class="material-icons text-6xl mb-4">{{ card.icon }}</span>
              <span class="font-bold text-xl">{{ card.name.replace('Carte Cadeau ', '') }}</span>
              <span class="text-sm opacity-80 mt-2">Carte Cadeau</span>
            </div>
            <div class="absolute bottom-0 left-0 right-0 bg-black/30 backdrop-blur-sm p-3">
              <p class="text-white font-bold text-center">À partir de {{ card.minPrice.toLocaleString() }} CFA</p>
            </div>
          </div>
          <h3 class="font-bold text-slate-900 dark:text-slate-100 group-hover:text-primary transition-colors">{{ card.name }}</h3>
          <p class="text-primary font-bold text-sm mt-1">{{ card.minPrice.toLocaleString() }} - {{ card.maxPrice.toLocaleString() }} CFA</p>
        </RouterLink>
      </div>

      <!-- Pagination -->
      <div class="mt-16 flex justify-center">
        <nav class="flex items-center space-x-2">
          <button class="w-10 h-10 flex items-center justify-center rounded-lg bg-primary text-white font-bold shadow-lg shadow-primary/30">1</button>
          <button class="w-10 h-10 flex items-center justify-center rounded-lg bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-400 hover:bg-slate-200 dark:hover:bg-slate-700 transition-colors">2</button>
          <button class="w-10 h-10 flex items-center justify-center rounded-lg bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-400 hover:bg-slate-200 dark:hover:bg-slate-700 transition-colors">
            <span class="material-icons-outlined text-xl">chevron_right</span>
          </button>
        </nav>
      </div>
    </section>

  </div>
</template>
